import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Unzip File
 *
 * Generic node that extracts the contents of a zip archive into one child
 * File per file entry. Directory entries are skipped (they carry no data);
 * files nested in subfolders are still uploaded as children with their
 * in-archive relative path preserved in `entryName`.
 *
 * Passthrough: when the input File is not a valid zip archive, it is
 * returned unchanged so downstream steps can consume it directly.
 */
export const unzipFileStep = defineStepConfig({
  type: "unzip_document",
  name: "Unzip File",
  description:
    "Extract a zip archive into one child file per entry (passes through unchanged if not a zip)",
  category: "Transformation",

  inputSchema: z.object({
    file: entityOfType(SystemEntity.CORE_FILE).describe(
      "The raw file to unzip (e.g. an email attachment before classification).",
    ),
  }),

  outputSchema: z.object({
    file: z
      .array(entityOfType(SystemEntity.CORE_FILE))
      .describe(
        "Child file per extracted entry, or the original file unchanged when not a zip archive",
      ),
    entryName: z
      .array(z.string())
      .describe(
        "Relative path of the file within the zip archive (original file name when passed through)",
      ),
    entryIndex: z
      .array(z.number().int().nonnegative())
      .describe("0-based position of the entry within the zip archive"),
  }),

  configSchema: z.object({
    fileFilter: z
      .string()
      .optional()
      .describe(
        "Optional regex pattern to filter which zip entry paths to extract (e.g. '\\.csv$' to only extract CSV files). Leave empty to extract all files.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "10 minutes",
    retry: {
      maximumAttempts: 2,
      backoffCoefficient: 2,
      initialInterval: "5 seconds",
    },
  },
});
